// src/views/analytics/ExecutiveOverviewDashboard.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Executive Overview (v5, round 4).
//
// --- UPDATE (round 4) ----------------------------------------------------------------------------
// Rebuilt on real, live data only. What changed against v4.1:
//  • KPI sparklines were Math.sin + Math.random (a new chart on every render) and the backend's
//    real `_trend` values were overwritten with 0. Sparklines now come from the daily KPI history
//    (GET /ml-ops/insights/kpi-history) and trends are the server's comparison with the previous
//    period. "Industry Avg 98.5 %", "Last Period 45", "Yesterday 3" and a "$100,000 budget" were
//    hard-coded — the comparison line is now the previous period's real value, and targets /
//    budget are your own settings (no budget → no budget line).
//  • Missing KPIs rendered as "null%" and `null < 95` raised false alerts; null now shows "—" and
//    alerts only fire on real values. Alert toasts fire when an alert first appears, not on every
//    refresh.
//  • The forecast's confidence was floored at 50 % and derived from R² minus a constant; it is a
//    linear trend with a 95 % prediction interval now, with R² shown as its fit. Hotspot intensity
//    was a constant 0.5 — it is the severity-weighted density of a ~1 km grid. The scorecard had a
//    fixed "efficiency 80"; it uses only measures that have data and says how many.
//  • Every refresh flashed skeletons. After the first load refreshes are quiet (a header spinner),
//    stale responses are dropped, and the page refetches itself (throttled) when a relevant live
//    event arrives (AnalyticsPage passes liveVersion / lastLiveEvent).
//  • New AI elements: AI asset-risk panel (RUL model, confidence, estimated cost), server AI
//    insights merged into the insight cards, "Ask FlowSense AI" on every KPI / risk / anomaly, real
//    AI engine status in the header and footer, live activity feed from the AI activity log, and an
//    executive briefing that falls back to the ML Ops briefing when /reports is unavailable.
//  • Settings work and persist (refresh, notifications, drill-down, map, targets, budget, cost
//    window); exports are real (multi-page PDF, CSV, JSON) and load their libraries on demand.
//  • Quick filters filter; view modes (Dashboard / Details / Compare) all render real data.
//  • data-guide anchors for the FlowSense AI Guide: exec-kpis, exec-cost, exec-risk,
//    exec-insights, exec-briefing.
// --- END UPDATE ---------------------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import {
  Box, SimpleGrid, Spinner, Alert, AlertIcon, AlertDescription, Heading, VStack, HStack, Button, ButtonGroup,
  IconButton, Tooltip, useToast, Badge, Progress, Flex, Text, useColorModeValue, useDisclosure,
  Tabs, TabList, TabPanels, Tab, TabPanel, Select, Switch, FormControl, FormLabel, Collapse, Code, Divider,
  Menu, MenuButton, MenuList, MenuItem, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
  ModalBody, ModalCloseButton, Skeleton, SkeletonText, Input, InputGroup, InputLeftElement, InputRightElement,
  NumberInput, NumberInputField, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Grid, Wrap, WrapItem, Center, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Icon,
  CircularProgress, CircularProgressLabel, Tag, TagLabel, useBreakpointValue,
} from '@chakra-ui/react';
import {
  Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, ComposedChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Cell,
} from 'recharts';
import {
  MdRefresh, MdDownload, MdSettings, MdBugReport, MdFullscreen, MdFullscreenExit, MdPrint,
  MdTrendingUp, MdTrendingDown, MdTrendingFlat, MdWarning, MdError, MdCheckCircle, MdInfo, MdDashboard,
  MdAnalytics, MdSpeed, MdStorage, MdMemory, MdViewList, MdCompareArrows, MdSearch, MdExpandMore,
  MdExpandLess, MdLightbulb, MdInsights, MdNotificationsActive, MdSmartToy, MdMap, MdLayers,
  MdLocationOn, MdSatellite, MdTerrain, MdClose, MdDescription, MdFilterList, MdAutorenew,
  MdQuestionAnswer, MdContentCopy,
} from 'react-icons/md';
import {
  FaChartBar, FaDatabase, FaExclamationTriangle, FaDollarSign, FaTools, FaCheckCircle, FaBrain,
  FaShieldAlt, FaStar, FaRobot,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import api from '../../services/api';
import analyticsService from '../../services/analyticsService';
import aiService from '../../services/aiService';
import mlOpsService from '../../services/mlOpsService';
import { useMlOverview } from '../../hooks/useMlOps';
import { timeAgo } from '../../components/ml/mlFormat';
import DashboardAIInsights from '../../components/analytics/DashboardAIInsights';
import AIMetricsDashboard from '../../components/analytics/AIMetricsDashboard';
import KPIFullReportModal from '../../components/analytics/KPIFullReportModal';
import {
  isNum, num, fmtNum, fmtPct, fmtMoney, fmtSigned, resolvePeriod, periodSeries, seriesStats, analyseCost,
  buildHotspots, healthScore, kpiAlerts, liveEventToActivity, mergeActivity, markdownBlocks,
  briefingToMarkdown, toYmd,
} from './executiveAnalytics';

// Heavy children load on demand (the map pulls in Leaflet tiles; the anomaly widget its own data).
const GeospatialHotspotMap = lazy(() => import('../../components/analytics/GeospatialHotspotMap'));
const CostAnomalyWidget = lazy(() => import('../../components/analytics/CostAnomalyWidget'));

// --- Leaflet default-icon fix (idempotent; the hotspot map relies on it) ---
if (L?.Icon?.Default?.prototype && !L.Icon.Default.prototype._getIconUrl_original) {
  L.Icon.Default.prototype._getIconUrl_original = L.Icon.Default.prototype._getIconUrl;
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  });
}

const MotionBox = motion(Box);
const CHART = { primary: '#4F46E5', accent: '#06B6D4', success: '#10B981', warning: '#F59E0B', danger: '#EF4444', purple: '#805AD5', muted: '#CBD5E0' };
const SEV_COLOR = { critical: 'red', error: 'red', warning: 'orange', alert: 'orange', success: 'green', info: 'blue' };
const SEV_ICON = { critical: MdError, error: MdError, warning: MdWarning, alert: MdWarning, success: MdCheckCircle, info: MdInfo };

const SETTINGS_KEY = 'flowsense.exec.settings.v1';
const DEFAULT_SETTINGS = {
  autoRefresh: true, refreshInterval: 300000, notifications: true, drilldown: true, mapStyle: 'streets',
  showMapRadius: false, costMonths: 12, targets: { uptime: 99, sla: 95, budget: null }, favorites: [],
};
const loadSettings = (defaults) => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SETTINGS_KEY) : null;
    const saved = raw ? JSON.parse(raw) : null;
    return saved ? { ...defaults, ...saved, targets: { ...defaults.targets, ...(saved.targets || {}) } } : defaults;
  } catch (_) { return defaults; }
};
const saveSettings = (s) => { try { window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch (_) { /* private mode */ } };

// Live events that change what this page shows → quiet refetch (throttled).
const LIVE_REFETCH = new Set([
  'new_sensor_alert', 'critical_anomaly', 'asset_prediction_update', 'predictive_risk_update', 'predictive_analysis_complete',
  'rul_batch_complete', 'predictive_alerts_updated', 'asset_health_scores_updated', 'ml_events', 'ai_model_promoted',
]);
const LIVE_THROTTLE_MS = 15000;

const unwrapObj = (r) => (r && typeof r === 'object' && !Array.isArray(r) && r.data !== undefined && !Array.isArray(r.data) ? r.data : r);
const unwrapList = (r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.data?.data) ? r.data.data : []);

const KPI_DEFS = [
  { key: 'systemUptime', title: 'Asset Availability', unit: '%', icon: MdSpeed, color: 'green', better: 'higher', help: 'Share of live assets operational now', target: 'uptime' },
  { key: 'openWorkOrders', title: 'Open Work Orders', unit: 'count', icon: FaTools, color: 'blue', better: 'lower', help: 'Not completed, cancelled or rejected' },
  { key: 'slaCompliance', title: 'SLA Compliance', unit: '%', icon: FaCheckCircle, color: 'purple', better: 'higher', help: 'Work orders in the period within SLA', target: 'sla' },
  { key: 'totalMaintenanceCost', title: 'Maintenance Cost', unit: '$', icon: FaDollarSign, color: 'orange', better: 'lower', help: 'Completed maintenance in the period', target: 'budget' },
  { key: 'criticalAlerts', title: 'Critical Alerts', unit: 'count', icon: MdError, color: 'red', better: 'lower', help: 'Critical sensor alerts active now' },
];
const fmtKpi = (def, v) => (def.unit === '%' ? fmtPct(v) : def.unit === '$' ? fmtMoney(v) : fmtNum(v));

// ==================== PRIMITIVES ====================
const GlassCard = ({ children, ...props }) => {
  const bg = useColorModeValue('whiteAlpha.900', 'gray.800');
  const border = useColorModeValue('gray.100', 'gray.700');
  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" boxShadow="0 8px 32px rgba(0,0,0,0.06)" overflow="hidden" position="relative" {...props}>
      {children}
    </Box>
  );
};

const PulseIndicator = ({ color = 'green', size = '10px', label }) => (
  <HStack spacing={1}>
    <Box position="relative">
      <Box w={size} h={size} borderRadius="full" bg={`${color}.400`} />
      <MotionBox position="absolute" top={0} left={0} w={size} h={size} borderRadius="full" bg={`${color}.400`}
        animate={{ scale: [1, 2], opacity: [0.6, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: 'easeOut' }} />
    </Box>
    {label && <Text fontSize="xs" fontWeight="medium" color={`${color}.500`}>{label}</Text>}
  </HStack>
);

const SectionTitle = ({ icon, color = 'blue', title, subtitle, right }) => (
  <HStack justify="space-between" mb={4} align="flex-start" flexWrap="wrap" gap={2}>
    <HStack spacing={2}>
      <Box p={2} bg={`${color}.100`} borderRadius="lg"><Icon as={icon} color={`${color}.600`} boxSize={5} /></Box>
      <VStack align="start" spacing={0}>
        <Heading size="sm">{title}</Heading>
        {subtitle && <Text fontSize="xs" color="gray.500">{subtitle}</Text>}
      </VStack>
    </HStack>
    {right}
  </HStack>
);

const EmptyState = ({ icon = MdInfo, text }) => (
  <VStack py={8} spacing={2} color="gray.500"><Icon as={icon} boxSize={8} color="gray.300" /><Text fontSize="sm" textAlign="center">{text}</Text></VStack>
);

// Recharts tooltip formatter that never prints "null".
const moneyTip = (v, name) => [isNum(v) ? fmtMoney(v) : '—', name];

// ==================== KPI CARD ====================
const KpiCard = ({ def, value, trend, sparkline, previous, target, alert, isLoading, isFavorite, onToggleFavorite, onClick, onAsk }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const iconBg = useColorModeValue(`${def.color}.50`, `${def.color}.900`);
  const [hover, setHover] = useState(false);
  const t = num(trend);
  const good = t === null || t === 0 ? null : (t > 0) === (def.better === 'higher');
  const tgt = num(target);
  const v = num(value);
  const progress = tgt && v !== null ? (def.better === 'lower' ? Math.min((tgt / Math.max(v, 1e-9)) * 100, 100) : Math.min((v / tgt) * 100, 100)) : null;
  const spark = (sparkline || []).filter(p => isNum(p.value));

  return (
    <Box p={5} shadow="md" borderWidth="1px" borderRadius="xl" bg={bg} borderColor={hover ? `${def.color}.300` : border}
      cursor={onClick ? 'pointer' : 'default'} onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      transition="all 0.2s" position="relative" overflow="hidden" data-testid={`kpi-${def.key}`}
      role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}>
      <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient={`linear(to-r, ${def.color}.400, ${def.color}.600)`} />
      <HStack position="absolute" top={2} right={2} spacing={0} zIndex={2} opacity={hover || isFavorite ? 1 : 0} transition="opacity 0.2s">
        {onAsk && (
          <Tooltip label="Ask FlowSense AI about this KPI" hasArrow>
            <IconButton icon={<MdQuestionAnswer />} size="xs" variant="ghost" colorScheme="purple" aria-label={`Ask AI about ${def.title}`}
              onClick={(e) => { e.stopPropagation(); onAsk(); }} />
          </Tooltip>
        )}
        <IconButton icon={<FaStar />} size="xs" variant="ghost" colorScheme={isFavorite ? 'yellow' : 'gray'} aria-label={`Star ${def.title}`}
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }} />
      </HStack>
      <Flex justify="space-between" align="start" mb={2}>
        <HStack spacing={3}>
          <Box p={2.5} bg={iconBg} borderRadius="lg"><Icon as={def.icon} color={`${def.color}.500`} boxSize={5} /></Box>
          <VStack align="start" spacing={0}>
            <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">{def.title}</Text>
            {isLoading ? <Skeleton height="30px" width="100px" borderRadius="md" /> : (
              <HStack spacing={2}>
                <Text fontSize="2xl" fontWeight="bold" lineHeight="short" data-testid={`kpi-${def.key}-value`}>{fmtKpi(def, value)}</Text>
                {t !== null && t !== 0 && (
                  <Tooltip label="Change vs the previous period of the same length" hasArrow>
                    <Badge colorScheme={good ? 'green' : 'red'} variant="subtle" borderRadius="full" px={2} data-testid={`kpi-${def.key}-trend`}>
                      <HStack spacing={0.5}><Icon as={t > 0 ? MdTrendingUp : MdTrendingDown} boxSize={3} /><Text fontSize="xs" fontWeight="bold">{Math.abs(t).toFixed(1)}%</Text></HStack>
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
            )}
          </VStack>
        </HStack>
        {alert && (
          <Tooltip label={alert.message} hasArrow>
            <Box color={alert.status === 'error' ? 'red.500' : 'orange.500'} pr={hover ? 14 : 0}><Icon as={alert.status === 'error' ? MdError : MdWarning} boxSize={5} /></Box>
          </Tooltip>
        )}
      </Flex>
      <Box height="46px" mt={1} mb={1} data-testid={`kpi-${def.key}-spark`}>
        {spark.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
            <AreaChart data={spark} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
              <defs><linearGradient id={`spark-${def.key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={`var(--chakra-colors-${def.color}-400)`} stopOpacity={0.4} /><stop offset="95%" stopColor={`var(--chakra-colors-${def.color}-400)`} stopOpacity={0} /></linearGradient></defs>
              <Area type="monotone" dataKey="value" stroke={`var(--chakra-colors-${def.color}-400)`} fill={`url(#spark-${def.key})`} strokeWidth={2} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Flex h="100%" align="center"><Text fontSize="xs" color="gray.400">{isLoading ? '' : 'No daily history for this KPI'}</Text></Flex>
        )}
      </Box>
      {progress !== null && (
        <Box mt={1}>
          <HStack justify="space-between" fontSize="xs" color="gray.500"><Text>{def.key === 'totalMaintenanceCost' ? 'Budget' : 'Target'}: {fmtKpi(def, tgt)}</Text><Text fontWeight="bold" color={progress >= 100 ? 'green.500' : 'orange.500'}>{progress.toFixed(0)}%</Text></HStack>
          <Progress value={progress} size="xs" colorScheme={progress >= 100 ? 'green' : 'orange'} mt={1} borderRadius="full" />
        </Box>
      )}
      <HStack mt={2} fontSize="xs" color="gray.500" justify="space-between">
        <Text>{def.help}</Text>
        {isNum(previous) && <Text title="Previous period, from the daily KPI history">prev: <b>{fmtKpi(def, previous)}</b></Text>}
      </HStack>
    </Box>
  );
};

// ==================== HEALTH SCORECARD ====================
const HealthScorecard = ({ health }) => {
  const border = useColorModeValue('gray.200', 'gray.700');
  const track = useColorModeValue('gray.100', 'gray.700');
  const color = health.score === null ? 'gray' : health.score >= 90 ? 'green' : health.score >= 75 ? 'blue' : health.score >= 60 ? 'yellow' : 'red';
  const radar = health.measures.map(m => ({ metric: m.label, value: Math.round(m.value) }));
  return (
    <GlassCard data-testid="health-scorecard">
      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }}>
        <Box p={6} textAlign="center" borderRightWidth={{ base: 0, md: '1px' }} borderBottomWidth={{ base: '1px', md: 0 }} borderColor={border}>
          <Heading size="sm" mb={4} color="gray.600">Operational Health Score</Heading>
          <CircularProgress value={health.score ?? 0} size="150px" thickness="8px" color={`${color}.400`} trackColor={track}>
            <CircularProgressLabel>
              <VStack spacing={0}><Text fontSize="4xl" fontWeight="black" color={`${color}.500`}>{health.score ?? '—'}</Text><Text fontSize="xs" color="gray.500" fontWeight="bold">/100</Text></VStack>
            </CircularProgressLabel>
          </CircularProgress>
          <Badge colorScheme={color} variant="subtle" borderRadius="full" px={4} py={1} mt={3} fontSize="sm">{health.label}</Badge>
          <Text fontSize="xs" color="gray.500" mt={2}>Based on {health.used} of {health.possible} measures with data</Text>
        </Box>
        <Box p={4}>
          <Heading size="xs" mb={2} textAlign="center" color="gray.500">Score breakdown</Heading>
          {radar.length >= 3 ? (
            <Box height="170px">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <RadarChart data={radar} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid strokeDasharray="3 3" /><PolarAngleAxis dataKey="metric" fontSize={11} /><PolarRadiusAxis domain={[0, 100]} tick={false} />
                  <Radar dataKey="value" stroke={CHART.primary} fill={CHART.primary} fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          ) : null}
          <VStack align="stretch" spacing={1} mt={radar.length >= 3 ? 0 : 4}>
            {health.measures.map(m => (
              <HStack key={m.key} justify="space-between" fontSize="xs"><Text color="gray.600">{m.label}</Text><Text color="gray.500" noOfLines={1} title={m.basis}>{m.basis}</Text><Text fontWeight="bold">{Math.round(m.value)}</Text></HStack>
            ))}
            {health.measures.length === 0 && <Text fontSize="xs" color="gray.500" textAlign="center">No KPI data yet.</Text>}
          </VStack>
        </Box>
      </Grid>
    </GlassCard>
  );
};

// ==================== COST TREND + FORECAST ====================
const CostTrendCard = ({ analysis, loading, months, onAsk }) => {
  const predBg = useColorModeValue('purple.50', 'purple.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const { chart, anomalies, forecast } = analysis;
  const data = useMemo(() => {
    const rows = chart.map(r => ({ ...r }));
    if (forecast) {
      if (rows.length) rows[rows.length - 1] = { ...rows[rows.length - 1], predicted: rows[rows.length - 1].totalCost };
      forecast.points.forEach(p => rows.push({ month: p.month, predicted: p.predicted, band: [p.lower, p.upper] }));
    }
    return rows;
  }, [chart, forecast]);

  return (
    <GlassCard p={5} data-guide="exec-cost" data-testid="cost-trend">
      <SectionTitle icon={FaDollarSign} color="blue" title="Maintenance cost trend" subtitle={`Monthly spend, last ${months} months · SMA/EMA(3), 2σ bands`}
        right={(
          <HStack spacing={2}>
            {anomalies.length > 0 && <Badge colorScheme="red" variant="subtle" borderRadius="full">{anomalies.length} outlier{anomalies.length === 1 ? '' : 's'}</Badge>}
            {forecast && <Badge colorScheme="purple" variant="subtle" borderRadius="full"><HStack spacing={1}><Icon as={FaBrain} boxSize={3} /><Text>Forecast</Text></HStack></Badge>}
          </HStack>
        )} />
      {loading ? <Skeleton height="300px" borderRadius="lg" /> : chart.length === 0 ? (
        <EmptyState icon={FaDollarSign} text="No maintenance cost recorded in this window." />
      ) : (
        <Box height="300px" width="100%">
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
            <ComposedChart data={data}>
              <defs><linearGradient id="execCost" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={CHART.primary} stopOpacity={0.3} /><stop offset="95%" stopColor={CHART.primary} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="month" fontSize={11} tickLine={false} />
              <YAxis fontSize={11} tickLine={false} tickFormatter={(v) => fmtMoney(v, { compact: true })} width={56} />
              <RechartsTooltip formatter={(v, name) => (Array.isArray(v) ? [`${fmtMoney(v[0])} – ${fmtMoney(v[1])}`, name] : moneyTip(v, name))} contentStyle={{ borderRadius: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="upperBand" stroke="none" fill={CHART.purple} fillOpacity={0.06} name="Upper 2σ" connectNulls={false} />
              <Area type="monotone" dataKey="lowerBand" stroke="none" fill={CHART.purple} fillOpacity={0.06} name="Lower 2σ" connectNulls={false} />
              {forecast && <Area type="monotone" dataKey="band" stroke="none" fill={CHART.purple} fillOpacity={0.15} name="95% interval" />}
              <Area type="monotone" dataKey="totalCost" stroke={CHART.primary} strokeWidth={3} fill="url(#execCost)" name="Actual" connectNulls={false} />
              <Line type="monotone" dataKey="sma" stroke={CHART.warning} strokeWidth={2} strokeDasharray="5 5" name="SMA(3)" dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="ema" stroke={CHART.success} strokeWidth={2} strokeDasharray="3 3" name="EMA(3)" dot={false} connectNulls={false} />
              {forecast && <Line type="monotone" dataKey="predicted" stroke={CHART.purple} strokeWidth={3} strokeDasharray="8 4" name="Forecast" dot={{ r: 4 }} connectNulls />}
              <Scatter dataKey="anomaly" fill={CHART.danger} name="Outlier" />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
      {forecast && (
        <Box mt={4} p={4} bg={predBg} borderRadius="xl" data-testid="cost-forecast">
          <HStack justify="space-between" mb={3} flexWrap="wrap">
            <HStack spacing={2}><Icon as={FaBrain} color="purple.500" /><Text fontSize="sm" fontWeight="bold">Cost forecast</Text></HStack>
            <HStack spacing={2}>
              <Tooltip label="R² — how much of the month-to-month variation the trend line explains. Low R² means the forecast is a rough guide." hasArrow>
                <Badge colorScheme={forecast.r2 !== null && forecast.r2 >= 0.6 ? 'green' : 'yellow'} variant="subtle" borderRadius="full">fit R² {forecast.r2 === null ? '—' : `${(forecast.r2 * 100).toFixed(0)}%`}</Badge>
              </Tooltip>
              <Badge variant="outline" borderRadius="full" fontWeight="normal" textTransform="none">{forecast.method}, {forecast.n} months</Badge>
            </HStack>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            {forecast.points.map(p => (
              <Box key={p.month} p={3} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={border}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">{p.month}</Text>
                <Text fontSize="lg" fontWeight="bold" mt={1}>{fmtMoney(p.predicted)}</Text>
                <Text fontSize="xs" color="gray.500">95%: {fmtMoney(p.lower)} – {fmtMoney(p.upper)}</Text>
              </Box>
            ))}
          </SimpleGrid>
          {onAsk && <Button mt={3} size="xs" variant="link" colorScheme="purple" leftIcon={<MdQuestionAnswer />} onClick={() => onAsk(`Maintenance cost is forecast at ${fmtMoney(forecast.points[0].predicted)} next month (95% interval ${fmtMoney(forecast.points[0].lower)}–${fmtMoney(forecast.points[0].upper)}, R² ${forecast.r2 === null ? 'n/a' : forecast.r2.toFixed(2)}). What drives this and what can we do?`)}>Ask FlowSense AI about the forecast</Button>}
        </Box>
      )}
      {!loading && chart.length > 0 && !forecast && <Text mt={3} fontSize="xs" color="gray.500">A forecast needs at least 4 months of cost history.</Text>}
    </GlassCard>
  );
};

// ==================== TREND ANALYSIS ====================
const TrendAnalysisCard = ({ trend }) => {
  const accent = useColorModeValue('purple.50', 'purple.900');
  if (!trend) return null;
  const color = trend.direction === 'upward' ? 'orange' : trend.direction === 'downward' ? 'green' : 'blue';
  const icon = trend.direction === 'upward' ? MdTrendingUp : trend.direction === 'downward' ? MdTrendingDown : MdTrendingFlat;
  const cells = [
    { label: 'Total change', value: fmtSigned(trend.totalChange), color: isNum(trend.totalChange) && trend.totalChange > 0 ? 'orange.500' : 'green.500' },
    { label: 'Volatility (CV)', value: fmtPct(trend.volatility), color: isNum(trend.volatility) && trend.volatility > 20 ? 'red.500' : 'green.500' },
    { label: 'Trend fit (R²)', value: trend.r2 === null ? '—' : `${(trend.r2 * 100).toFixed(0)}%` },
    { label: 'Slope / month', value: `${trend.slope > 0 ? '+' : ''}${fmtMoney(trend.slope)}` },
  ];
  return (
    <GlassCard p={5} data-testid="trend-analysis">
      <SectionTitle icon={icon} color={color} title="Trend analysis" subtitle="Least-squares regression on monthly spend"
        right={<Badge colorScheme={color} variant="subtle" borderRadius="full" px={3} textTransform="capitalize">{trend.direction}</Badge>} />
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        {cells.map(c => <Box key={c.label} p={3} bg={accent} borderRadius="lg"><Text fontSize="xs" color="gray.500">{c.label}</Text><Text fontSize="lg" fontWeight="bold" color={c.color}>{c.value}</Text></Box>)}
      </SimpleGrid>
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2} fontSize="xs">
        <Text color="gray.500">Mean: <b>{fmtMoney(trend.mean)}</b></Text>
        <Text color="gray.500">Std dev: <b>{fmtMoney(trend.sd)}</b></Text>
        <Text color="gray.500">Range: <b>{fmtMoney(trend.min)} – {fmtMoney(trend.max)}</b></Text>
        <Text color="gray.500">Months: <b>{trend.n}</b></Text>
      </SimpleGrid>
    </GlassCard>
  );
};

// ==================== ANOMALY TABLE ====================
const AnomalyTable = ({ anomalies, onAsk }) => {
  const hover = useColorModeValue('gray.50', 'gray.700');
  return (
    <GlassCard p={5} data-testid="anomaly-table">
      <SectionTitle icon={FaExclamationTriangle} color="red" title="Cost outliers" subtitle="Months more than 2σ from the other months"
        right={<Badge colorScheme={anomalies.length ? 'red' : 'green'} variant="subtle" borderRadius="full">{anomalies.length} found</Badge>} />
      {anomalies.length === 0 ? <EmptyState icon={MdCheckCircle} text="No month stands out from the rest (needs at least 4 months)." /> : (
        <TableContainer>
          <Table size="sm">
            <Thead><Tr><Th>Month</Th><Th isNumeric>Cost</Th><Th isNumeric>vs mean</Th><Th isNumeric>z</Th><Th>Severity</Th>{onAsk && <Th />}</Tr></Thead>
            <Tbody>
              {anomalies.map(a => (
                <Tr key={a.month} _hover={{ bg: hover }}>
                  <Td fontSize="xs" fontWeight="medium">{a.month}</Td>
                  <Td fontSize="xs" isNumeric fontWeight="bold">{fmtMoney(a.totalCost)}</Td>
                  <Td fontSize="xs" isNumeric color={a.change > 0 ? 'red.500' : 'green.500'}>{fmtSigned(a.change)}</Td>
                  <Td fontSize="xs" isNumeric>{a.zScore.toFixed(2)}</Td>
                  <Td><Badge colorScheme={a.severity === 'critical' ? 'red' : a.severity === 'high' ? 'orange' : 'yellow'} variant="subtle" borderRadius="full">{a.severity}</Badge></Td>
                  {onAsk && <Td><IconButton size="xs" variant="ghost" colorScheme="purple" icon={<MdQuestionAnswer />} aria-label={`Ask about ${a.month}`} onClick={() => onAsk(`Maintenance spend in ${a.month} was ${fmtMoney(a.totalCost)}, ${a.zScore.toFixed(1)} standard deviations from the other months (mean ${fmtMoney(a.baselineMean)}). What could explain it?`)} /></Td>}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </GlassCard>
  );
};

// ==================== TOP COST ASSETS ====================
const TopCostAssets = ({ rows, loading }) => {
  const data = (rows || []).filter(r => isNum(r.total_cost) && Number(r.total_cost) > 0).slice(0, 8)
    .map(r => ({ name: String(r.name || r.asset_id).slice(0, 22), cost: Number(r.total_cost), workOrders: num(r.work_order_count) }));
  return (
    <GlassCard p={5} data-testid="top-cost-assets">
      <SectionTitle icon={FaChartBar} color="orange" title="Highest-cost assets" subtitle="Maintenance + work-order spend in the period" />
      {loading ? <Skeleton height="220px" /> : data.length === 0 ? <EmptyState text="No asset spend recorded in this period." /> : (
        <Box height={`${Math.max(160, data.length * 32)}px`}>
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
            <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
              <XAxis type="number" fontSize={11} tickFormatter={(v) => fmtMoney(v, { compact: true })} />
              <YAxis type="category" dataKey="name" fontSize={11} width={120} />
              <RechartsTooltip formatter={(v, n, p) => [fmtMoney(v), `${p?.payload?.workOrders ?? '—'} work orders`]} />
              <Bar dataKey="cost" radius={[0, 4, 4, 0]}>{data.map((d, i) => <Cell key={d.name} fill={i === 0 ? CHART.danger : i < 3 ? CHART.warning : CHART.primary} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </GlassCard>
  );
};

// ==================== AI ASSET RISK (RUL model) ====================
const AssetRiskPanel = ({ risk, loading, error, onAsk }) => {
  const rowHover = useColorModeValue('gray.50', 'gray.700');
  const summary = risk?.summary;
  const assets = risk?.assets || [];
  return (
    <GlassCard p={5} data-guide="exec-risk" data-testid="asset-risk">
      <SectionTitle icon={FaRobot} color="purple" title="AI asset risk" subtitle="Latest RUL model prediction per asset, riskiest first"
        right={summary ? <Badge colorScheme={summary.critical ? 'red' : 'green'} variant="subtle" borderRadius="full">{summary.critical} critical · {summary.warning} warning</Badge> : null} />
      {loading ? <SkeletonText noOfLines={6} spacing={3} /> : error ? (
        <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />Asset risk is unavailable: {error}</Alert>
      ) : assets.length === 0 ? (
        <EmptyState icon={FaRobot} text="No active RUL predictions yet. Run a predictive analysis from the Predictive Health tab." />
      ) : (
        <>
          {summary && (
            <SimpleGrid columns={3} spacing={2} mb={3} fontSize="xs">
              <Box><Text color="gray.500">Assets scored</Text><Text fontWeight="bold" fontSize="md">{fmtNum(summary.total)}</Text></Box>
              <Box><Text color="gray.500">Avg confidence</Text><Text fontWeight="bold" fontSize="md">{summary.avgConfidence === null ? '—' : fmtPct(summary.avgConfidence * 100, 0)}</Text></Box>
              <Box><Text color="gray.500">Cost exposure</Text><Text fontWeight="bold" fontSize="md">{fmtMoney(summary.exposureCost, { compact: true })}</Text></Box>
            </SimpleGrid>
          )}
          <VStack align="stretch" spacing={1}>
            {assets.map(a => {
              const r = num(a.riskScore);
              const color = r === null ? 'gray' : r >= 80 ? 'red' : r >= 60 ? 'orange' : r >= 40 ? 'yellow' : 'green';
              return (
                <HStack key={a.assetId} p={2} borderRadius="md" _hover={{ bg: rowHover }} spacing={3} data-testid={`risk-${a.assetId}`}>
                  <Box w="44px" textAlign="center"><Badge colorScheme={color} variant="solid" borderRadius="md" fontSize="xs">{r === null ? '—' : r}</Badge></Box>
                  <VStack align="start" spacing={0} flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{a.assetName}</Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {a.assetType} · RUL {isNum(a.rulDays) ? `${a.rulDays} d` : '—'} · conf. {isNum(a.confidence) ? `${a.confidence}%` : '—'}
                      {isNum(a.estimatedCost) ? ` · est. ${fmtMoney(a.estimatedCost)}` : ''}{a.riskBasis === 'rul_rule' ? ' · rule-based risk' : ''}
                    </Text>
                  </VStack>
                  {onAsk && <IconButton size="xs" variant="ghost" colorScheme="purple" icon={<MdQuestionAnswer />} aria-label={`Ask why ${a.assetName} is at risk`}
                    onClick={() => onAsk(`Why is ${a.assetName} (${a.assetType}) at risk? The RUL model predicts ${isNum(a.rulDays) ? `${a.rulDays} days` : 'an unknown time'} remaining with ${isNum(a.confidence) ? `${a.confidence}%` : 'unknown'} confidence and a risk score of ${r ?? 'unknown'}. What should we do?`)} />}
                </HStack>
              );
            })}
          </VStack>
          {summary?.lastPredictionAt && <Text mt={2} fontSize="xs" color="gray.500">Last prediction {timeAgo(summary.lastPredictionAt)}</Text>}
        </>
      )}
    </GlassCard>
  );
};

// ==================== ACTIVITY FEED ====================
const ActivityFeed = ({ items, loading, liveConnected }) => {
  const hover = useColorModeValue('gray.50', 'gray.700');
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? items : items.slice(0, 8);
  return (
    <GlassCard p={5} data-testid="activity-feed">
      <SectionTitle icon={MdNotificationsActive} color="green" title="Live activity" subtitle={`${items.length} recent AI and operations events`}
        right={<PulseIndicator color={liveConnected ? 'green' : 'gray'} label={liveConnected ? 'Live' : 'Polling'} />} />
      {loading && items.length === 0 ? <SkeletonText noOfLines={5} spacing={3} /> : items.length === 0 ? <EmptyState icon={MdCheckCircle} text="No AI or operations events yet." /> : (
        <VStack align="stretch" spacing={1} maxH="420px" overflowY="auto">
          <AnimatePresence initial={false}>
            {shown.map(e => {
              const c = SEV_COLOR[e.severity] || 'gray';
              return (
                <MotionBox key={e.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                  <HStack spacing={3} p={2} borderRadius="lg" _hover={{ bg: hover }} borderLeftWidth="3px" borderLeftColor={`${c}.400`} align="start">
                    <Icon as={SEV_ICON[e.severity] || MdInfo} color={`${c}.500`} boxSize={4} mt={0.5} />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium" lineHeight="short">{e.title}</Text>
                      {e.detail && <Text fontSize="xs" color="gray.500" noOfLines={2}>{e.detail}</Text>}
                      <Text fontSize="xs" color="gray.400">{timeAgo(e.at)}{e.source === 'live' ? ' · live' : ''}</Text>
                    </VStack>
                  </HStack>
                </MotionBox>
              );
            })}
          </AnimatePresence>
        </VStack>
      )}
      {items.length > 8 && <Button size="xs" variant="ghost" mt={2} w="full" onClick={() => setShowAll(s => !s)} rightIcon={showAll ? <MdExpandLess /> : <MdExpandMore />}>{showAll ? 'Show less' : `Show all (${items.length})`}</Button>}
    </GlassCard>
  );
};

// ==================== DATA QUALITY ====================
const DataQuality = ({ kpis, history, costRows, hot, aiStatus }) => {
  const track = useColorModeValue('gray.100', 'gray.700');
  const items = useMemo(() => {
    const kpiVals = KPI_DEFS.map(d => num(kpis?.[d.key]));
    const kpiHave = kpiVals.filter(v => v !== null).length;
    const histHave = KPI_DEFS.filter(d => Array.isArray(history?.series?.[d.key])).length;
    const months = (costRows || []).length;
    const geo = hot.located + hot.unlocated ? (hot.located / (hot.located + hot.unlocated)) * 100 : null;
    return [
      { label: 'KPI completeness', value: (kpiHave / KPI_DEFS.length) * 100, detail: `${kpiHave}/${KPI_DEFS.length} KPIs have data` },
      { label: 'Daily KPI history', value: (histHave / KPI_DEFS.length) * 100, detail: history?.unavailable?.length ? `no history: ${history.unavailable.join(', ')}` : `${histHave}/${KPI_DEFS.length} series` },
      { label: 'Cost history depth', value: Math.min(100, (months / 12) * 100), detail: `${months} month${months === 1 ? '' : 's'} (12 for a full year)` },
      { label: 'Issue geolocation', value: geo, detail: geo === null ? 'no issues in period' : `${hot.located} of ${hot.located + hot.unlocated} issues located` },
      { label: 'AI engine', value: aiStatus === 'ok' ? 100 : aiStatus === 'degraded' ? 50 : aiStatus === 'offline' ? 0 : null, detail: aiStatus || 'unknown' },
    ];
  }, [kpis, history, costRows, hot, aiStatus]);
  const scored = items.filter(i => i.value !== null);
  const overall = scored.length ? scored.reduce((a, i) => a + i.value, 0) / scored.length : null;
  const colorOf = (v) => (v === null ? 'gray' : v >= 90 ? 'green' : v >= 60 ? 'yellow' : 'red');
  return (
    <GlassCard p={5} data-testid="data-quality">
      <SectionTitle icon={FaShieldAlt} color="teal" title="Data quality" subtitle="How complete the data behind this page is"
        right={<Badge colorScheme={colorOf(overall)} variant="subtle" borderRadius="full" px={3}>{overall === null ? '—' : `${overall.toFixed(0)}%`}</Badge>} />
      <VStack align="stretch" spacing={3}>
        {items.map(i => (
          <Box key={i.label}>
            <HStack justify="space-between" mb={1}><Text fontSize="xs" fontWeight="medium">{i.label}</Text><Text fontSize="xs" color="gray.500" noOfLines={1}>{i.detail}</Text></HStack>
            <Progress value={i.value ?? 0} size="sm" colorScheme={colorOf(i.value)} borderRadius="full" bg={track} />
          </Box>
        ))}
      </VStack>
    </GlassCard>
  );
};

// ==================== HOTSPOT STATS ====================
const HotspotStats = ({ hot }) => {
  const redBg = useColorModeValue('red.50', 'red.900');
  const orangeBg = useColorModeValue('orange.50', 'orange.900');
  const greenBg = useColorModeValue('green.50', 'green.900');
  const { hotspots } = hot;
  const types = useMemo(() => {
    const t = {};
    hotspots.forEach(h => Object.entries(h.types).forEach(([k, v]) => { t[k] = (t[k] || 0) + v; }));
    return Object.entries(t).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [hotspots]);
  const high = hotspots.filter(h => h.intensity > 0.7).length;
  const mid = hotspots.filter(h => h.intensity > 0.4 && h.intensity <= 0.7).length;
  return (
    <GlassCard p={5} data-testid="hotspot-stats">
      <SectionTitle icon={MdLocationOn} color="orange" title="Issue hotspots" subtitle={`Severity-weighted density, ~${Math.round(hot.cellDeg * 111)} km grid`} />
      {hotspots.length === 0 ? <EmptyState icon={MdLocationOn} text="No geolocated issues in this period." /> : (
        <>
          <SimpleGrid columns={3} spacing={2} mb={3}>
            <Box p={3} bg={redBg} borderRadius="lg"><Text fontSize="xs" color="gray.500">Dense</Text><Text fontSize="xl" fontWeight="bold" color="red.500">{high}</Text></Box>
            <Box p={3} bg={orangeBg} borderRadius="lg"><Text fontSize="xs" color="gray.500">Moderate</Text><Text fontSize="xl" fontWeight="bold" color="orange.500">{mid}</Text></Box>
            <Box p={3} bg={greenBg} borderRadius="lg"><Text fontSize="xs" color="gray.500">Scattered</Text><Text fontSize="xl" fontWeight="bold" color="green.500">{hotspots.length - high - mid}</Text></Box>
          </SimpleGrid>
          <Text fontSize="xs" color="gray.500">Densest cell: <b>{hotspots[0].count}</b> issues near {hotspots[0].position[0].toFixed(3)}, {hotspots[0].position[1].toFixed(3)}</Text>
          {types.length > 0 && (
            <Wrap spacing={2} mt={3}>
              {types.map(([t, c], i) => <WrapItem key={t}><Tag size="sm" variant="subtle" colorScheme={['blue', 'green', 'purple', 'orange', 'red', 'teal'][i % 6]} borderRadius="full"><TagLabel>{t}: {c}</TagLabel></Tag></WrapItem>)}
            </Wrap>
          )}
        </>
      )}
    </GlassCard>
  );
};

// ==================== PERIOD COMPARISON ====================
const PeriodComparison = ({ series, period }) => {
  const [kpiKey, setKpiKey] = useState('openWorkOrders');
  const def = KPI_DEFS.find(d => d.key === kpiKey);
  const s = series?.[kpiKey];
  const data = useMemo(() => {
    if (!s?.available) return [];
    const n = Math.max(s.current.length, s.previous.length);
    return Array.from({ length: n }, (_, i) => ({ day: `Day ${i + 1}`, current: s.current[i]?.value ?? null, previous: s.previous[i]?.value ?? null }));
  }, [s]);
  return (
    <GlassCard p={5} data-testid="period-comparison">
      <SectionTitle icon={MdCompareArrows} color="blue" title="Period comparison" subtitle={`${period.start} → ${period.end} vs ${period.prevStart} → ${period.prevEnd}`}
        right={(
          <Select size="sm" w="200px" value={kpiKey} onChange={(e) => setKpiKey(e.target.value)} aria-label="KPI to compare">
            {KPI_DEFS.map(d => <option key={d.key} value={d.key}>{d.title}</option>)}
          </Select>
        )} />
      {!s?.available || data.length === 0 ? <EmptyState text={`No daily history for ${def.title}.`} /> : (
        <>
          <HStack spacing={6} mb={3} fontSize="sm">
            <Text>This period: <b>{fmtKpi(def, s.currentAgg)}</b></Text>
            <Text color="gray.500">Previous: <b>{fmtKpi(def, s.previousAgg)}</b></Text>
          </HStack>
          <Box height="260px">
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="day" fontSize={10} interval="preserveStartEnd" />
                <YAxis fontSize={11} />
                <RechartsTooltip formatter={(v, name) => [isNum(v) ? fmtKpi(def, v) : '—', name]} />
                <Legend />
                <Bar dataKey="current" fill={CHART.primary} name="This period" radius={[3, 3, 0, 0]} />
                <Bar dataKey="previous" fill={CHART.muted} name="Previous period" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </GlassCard>
  );
};

// ==================== KPI DETAILS ACCORDION ====================
const KpiDetails = ({ cards }) => {
  const accent = useColorModeValue('gray.50', 'gray.700');
  const expanded = useColorModeValue('blue.50', 'blue.900');
  return (
    <GlassCard p={5} data-testid="kpi-details">
      <SectionTitle icon={MdViewList} color="blue" title="KPI details" subtitle="Daily history statistics for the selected period" />
      <Accordion allowMultiple defaultIndex={[0]}>
        {cards.map(c => {
          const st = seriesStats(c.sparkline);
          return (
            <AccordionItem key={c.def.key} border="none" mb={2}>
              <AccordionButton bg={accent} borderRadius="lg" _expanded={{ bg: expanded }}>
                <HStack flex={1} spacing={3}>
                  <Icon as={c.def.icon} color={`${c.def.color}.500`} />
                  <Text fontWeight="semibold" fontSize="sm" flex={1} textAlign="left">{c.def.title}</Text>
                  <Text fontWeight="bold">{fmtKpi(c.def, c.value)}</Text>
                  {isNum(c.trend) && <Badge colorScheme={(c.trend > 0) === (c.def.better === 'higher') ? 'green' : 'red'} variant="subtle" borderRadius="full">{fmtSigned(c.trend)}</Badge>}
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={3}>
                <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2} fontSize="xs">
                  <Box><Text color="gray.500">Days</Text><Text fontWeight="bold">{st.n}</Text></Box>
                  <Box><Text color="gray.500">Mean</Text><Text fontWeight="bold">{fmtNum(st.mean, 2)}</Text></Box>
                  <Box><Text color="gray.500">Std dev</Text><Text fontWeight="bold">{fmtNum(st.sd, 2)}</Text></Box>
                  <Box><Text color="gray.500">Min / Max</Text><Text fontWeight="bold">{fmtNum(st.min, 2)} / {fmtNum(st.max, 2)}</Text></Box>
                  <Box><Text color="gray.500">Variation</Text><Text fontWeight="bold">{fmtPct(st.cv)}</Text></Box>
                </SimpleGrid>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </GlassCard>
  );
};

// ==================== DEBUG ====================
const DebugPanel = ({ isOpen, onClose, logs, perf }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const rowBg = useColorModeValue('gray.50', 'gray.700');
  return (
    <Collapse in={isOpen} animateOpacity>
      <Box mt={2} p={4} bg={bg} borderWidth="1px" borderRadius="xl" boxShadow="lg" data-testid="debug-panel">
        <HStack justify="space-between" mb={3}>
          <HStack><Icon as={MdBugReport} color="red.400" /><Heading size="sm">Diagnostics</Heading><Badge borderRadius="full">{logs.length} entries</Badge></HStack>
          <IconButton size="xs" variant="ghost" icon={<MdClose />} onClick={onClose} aria-label="Close diagnostics" />
        </HStack>
        <Tabs size="sm" variant="soft-rounded" colorScheme="blue">
          <TabList><Tab fontSize="xs">Logs</Tab><Tab fontSize="xs">Network</Tab><Tab fontSize="xs">Performance</Tab></TabList>
          <TabPanels>
            <TabPanel px={0}>
              <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
                {logs.map((l, i) => <Code key={`${l.at}-${i}`} fontSize="xs" colorScheme={l.type === 'error' ? 'red' : l.type === 'warning' ? 'yellow' : l.type === 'success' ? 'green' : 'gray'} p={1.5} borderRadius="md">[{l.at}] {l.type.toUpperCase()}: {l.message}</Code>)}
                {logs.length === 0 && <Text fontSize="xs" color="gray.500">No log entries.</Text>}
              </VStack>
            </TabPanel>
            <TabPanel px={0}>
              <VStack align="stretch" spacing={1} fontSize="xs">
                {perf.requests.map((r, i) => (
                  <HStack key={`${r.endpoint}-${i}`} justify="space-between" p={1.5} bg={rowBg} borderRadius="md">
                    <Text fontWeight="medium">{r.endpoint}</Text><Badge colorScheme={r.ok ? 'green' : 'red'} borderRadius="full">{r.status}</Badge><Text color="gray.500">{r.ms} ms</Text>
                  </HStack>
                ))}
                {perf.requests.length === 0 && <Text color="gray.500">No requests yet.</Text>}
              </VStack>
            </TabPanel>
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} fontSize="xs">
                <HStack><Icon as={MdSpeed} color="gray.400" /><Text>Last load: <b>{perf.loadMs === null ? '—' : `${perf.loadMs} ms`}</b></Text></HStack>
                <HStack><Icon as={MdStorage} color="gray.400" /><Text>Requests: <b>{perf.requests.length}</b></Text></HStack>
                <HStack><Icon as={MdError} color="gray.400" /><Text>Failed: <b>{perf.requests.filter(r => !r.ok).length}</b></Text></HStack>
                <HStack><Icon as={MdMemory} color="gray.400" /><Text>JS heap: <b>{perf.heapMb === null ? 'n/a' : `${perf.heapMb} MB`}</b></Text></HStack>
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Collapse>
  );
};

// ==================== MARKDOWN (briefing) ====================
const MarkdownLite = ({ text }) => {
  const strong = useColorModeValue('blue.700', 'blue.200');
  const renderInline = (t) => String(t).split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
    /^\*\*[^*]+\*\*$/.test(part) ? <Text as="strong" key={i} color={strong}>{part.slice(2, -2)}</Text> : <React.Fragment key={i}>{part}</React.Fragment>
  ));
  return (
    <VStack align="stretch" spacing={2} data-testid="briefing-text">
      {markdownBlocks(text).map((b, i) => {
        if (b.kind === 'h1') return <Heading key={i} size="md" mt={i ? 3 : 0}>{renderInline(b.text)}</Heading>;
        if (b.kind === 'h2' || b.kind === 'h3') return <Heading key={i} size="sm" mt={2}>{renderInline(b.text)}</Heading>;
        if (b.kind === 'li') return <HStack key={i} align="start" spacing={2} pl={2}><Text>•</Text><Text fontSize="sm">{renderInline(b.text)}</Text></HStack>;
        return <Text key={i} fontSize="sm" lineHeight="tall">{renderInline(b.text)}</Text>;
      })}
    </VStack>
  );
};

// ==================== MAIN ====================
function ExecutiveOverviewDashboard({
  dateRange = null,
  liveVersion = 0,
  lastLiveEvent = null,
  onAskAI = null,
  refreshInterval: defaultRefreshInterval = 300000,
  enableDebug = true,
  enableExport = true,
  enableComparison = true,
}) {
  // ---- settings (persisted per browser) ----
  const [settings, setSettings] = useState(() => loadSettings({ ...DEFAULT_SETTINGS, refreshInterval: defaultRefreshInterval }));
  const updateSettings = useCallback((patch) => setSettings((s) => {
    const next = { ...s, ...patch, targets: { ...s.targets, ...(patch.targets || {}) } };
    saveSettings(next);
    return next;
  }), []);

  // ---- period ----
  const period = useMemo(
    () => resolvePeriod(dateRange),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toYmd(dateRange?.startDate ?? dateRange?.start), toYmd(dateRange?.endDate ?? dateRange?.end)],
  );

  // ---- data ----
  const [kpis, setKpis] = useState(null);
  const [history, setHistory] = useState(null);
  const [costRows, setCostRows] = useState([]);
  const [issues, setIssues] = useState([]);
  const [problemAssets, setProblemAssets] = useState([]);
  const [serverInsights, setServerInsights] = useState(null);
  const [risk, setRisk] = useState(null);
  const [riskError, setRiskError] = useState(null);
  const [aiModels, setAiModels] = useState(null);
  const [activity, setActivity] = useState([]);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ---- ui ----
  const [viewMode, setViewMode] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState([]);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [isFullReportOpen, setIsFullReportOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [logs, setLogs] = useState([]);
  const [perf, setPerf] = useState({ loadMs: null, requests: [], heapMb: null });
  const [mapKey, setMapKey] = useState('map-0');
  const [report, setReport] = useState({ text: '', source: null, loading: false, error: null });

  const settingsDlg = useDisclosure();
  const drilldownDlg = useDisclosure();
  const insightsDlg = useDisclosure();
  const monitorDlg = useDisclosure();
  const reportDlg = useDisclosure();
  const toast = useToast();

  // ---- refs ----
  const mounted = useRef(true);
  const reqSeq = useRef(0);
  const hasData = useRef(false);
  const dashboardRef = useRef(null);
  const knownAlerts = useRef(null);
  const lastLiveRefresh = useRef(0);
  const liveTimer = useRef(null);
  const fetchRef = useRef(null);

  // ---- theme (hoisted) ----
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const headerGradient = useColorModeValue('linear(to-r, blue.600, purple.600)', 'linear(to-r, blue.300, purple.300)');
  const modalBodyBg = useColorModeValue('gray.50', 'gray.900');
  const recommendBg = useColorModeValue('blue.50', 'blue.900');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const kpiColumns = useBreakpointValue({ base: 1, sm: 2, md: 3, xl: 5 }) || 1;

  // ---- AI status (control plane overview; polls + live) ----
  const { data: overview, error: overviewError } = useMlOverview();
  const aiHealth = useMemo(() => {
    if (overview?.mlEngine) {
      const online = Boolean(overview.mlEngine.online);
      const high = (overview.attention || []).some(a => a.level === 'high');
      return { status: online ? (high ? 'degraded' : 'ok') : 'offline', message: overview.mlEngine.error || null, assistantOnline: Boolean(overview.summary?.assistantOnline), inProduction: overview.summary?.inProduction ?? null, trainable: overview.summary?.trainable ?? null };
    }
    if (overviewError) return { status: 'unknown', message: overviewError.message };
    return null;
  }, [overview, overviewError]);

  useEffect(() => () => { mounted.current = false; if (liveTimer.current) clearTimeout(liveTimer.current); }, []);

  const log = useCallback((message, type = 'info') => {
    setLogs(prev => [{ at: new Date().toLocaleTimeString(), type, message }, ...prev].slice(0, 100));
  }, []);

  // ---- fetch ----
  const fetchAll = useCallback(async ({ quiet = false, reason = 'load' } = {}) => {
    const id = ++reqSeq.current;
    if (hasData.current && quiet) setRefreshing(true); else if (!hasData.current) setLoading(true); else setRefreshing(true);
    const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
    const requests = [];
    const timed = (endpoint, promise) => {
      const s = Date.now();
      return Promise.resolve(promise).then(
        (v) => { requests.push({ endpoint, status: 200, ok: true, ms: Date.now() - s }); return v; },
        (e) => { requests.push({ endpoint, status: e?.status || e?.response?.status || 'ERR', ok: false, ms: Date.now() - s }); throw e; },
      );
    };
    const params = { startDate: period.start, endDate: period.end };
    // Cost trend uses a longer window (months, ending at the period end) — a 30-day range has one
    // or two monthly points, which is not enough for bands, outliers or a forecast.
    const [endY, endM] = period.end.split('-').map(Number);
    const costParams = { startDate: new Date(Date.UTC(endY, endM - settings.costMonths, 1)).toISOString().slice(0, 10), endDate: period.end };

    const results = await Promise.allSettled([
      timed('/analytics/kpis', analyticsService.getKpis(params)),
      timed('/ml-ops/insights/kpi-history', mlOpsService.getKpiHistory(period.historyDays)),
      timed('/analytics/widgets/maintenance-cost-trend', analyticsService.getWidgetData('maintenance-cost-trend', costParams)),
      timed('/analytics/widgets/issue-hotspots', analyticsService.getWidgetData('issue-hotspots', params)),
      timed('/analytics/widgets/problem-assets', analyticsService.getWidgetData('problem-assets', params)),
      timed('/analytics/ai-insights', analyticsService.getAiInsights ? analyticsService.getAiInsights() : Promise.resolve([])),
      timed('/ml-ops/insights/asset-risk', mlOpsService.getAssetRisk({ limit: 8 })),
      timed('/ai/models', aiService.getAvailableModels({ status: 'production', limit: 50 })),
      timed('/ml-ops/activity', mlOpsService.getActivity({ limit: 25 })),
    ]);
    if (!mounted.current || id !== reqSeq.current) return; // a newer refresh superseded this one
    const [kpiR, histR, costR, hotR, probR, insR, riskR, modelsR, actR] = results;
    const bad = [];

    if (kpiR.status === 'fulfilled') setKpis(unwrapObj(kpiR.value) || {}); else bad.push('KPIs');
    if (histR.status === 'fulfilled') setHistory(histR.value || null); else bad.push('KPI history');
    if (costR.status === 'fulfilled') setCostRows(unwrapList(costR.value)); else bad.push('cost trend');
    if (hotR.status === 'fulfilled') setIssues(unwrapList(hotR.value)); else bad.push('issue hotspots');
    if (probR.status === 'fulfilled') setProblemAssets(unwrapList(probR.value)); else bad.push('top cost assets');
    if (insR.status === 'fulfilled') setServerInsights(Array.isArray(insR.value) ? insR.value : unwrapList(insR.value));
    if (riskR.status === 'fulfilled') { setRisk(riskR.value || null); setRiskError(null); } else setRiskError(riskR.reason?.message || 'request failed');
    if (modelsR.status === 'fulfilled') { const m = modelsR.value; setAiModels(m?.models || m?.data || (Array.isArray(m) ? m : [])); }
    if (actR.status === 'fulfilled') {
      const list = (Array.isArray(actR.value) ? actR.value : actR.value?.items || []).map(e => ({ id: `act-${e.id}`, severity: e.severity, title: e.title, detail: e.detail, link: e.link, at: e.at, source: 'log' }));
      setActivity(prev => mergeActivity(prev.filter(e => e.source !== 'log'), list));
    }
    setFailed(bad);
    const allCoreFailed = kpiR.status === 'rejected' && histR.status === 'rejected' && costR.status === 'rejected';
    if (!allCoreFailed) hasData.current = true;
    setLastUpdated(new Date());
    const loadMs = Math.round(((typeof performance !== 'undefined' ? performance : Date).now()) - t0);
    const heap = typeof performance !== 'undefined' && performance.memory ? Number((performance.memory.usedJSHeapSize / 1048576).toFixed(1)) : null;
    setPerf(p => ({ loadMs, heapMb: heap, requests: [...requests, ...p.requests].slice(0, 30) }));
    log(`${reason}: ${requests.filter(r => r.ok).length}/${requests.length} sources in ${loadMs} ms${bad.length ? ` — unavailable: ${bad.join(', ')}` : ''}`, bad.length ? 'warning' : 'success');
    setLoading(false); setRefreshing(false);
  }, [period, settings.costMonths, log]);

  fetchRef.current = fetchAll;
  useEffect(() => { fetchAll({ reason: 'load' }); }, [fetchAll]);

  // ---- auto refresh (skips hidden tabs) ----
  useEffect(() => {
    if (!settings.autoRefresh || !settings.refreshInterval) return undefined;
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchRef.current?.({ quiet: true, reason: 'auto-refresh' });
    }, settings.refreshInterval);
    return () => clearInterval(t);
  }, [settings.autoRefresh, settings.refreshInterval]);

  // ---- live events from AnalyticsPage ----
  useEffect(() => {
    if (!liveVersion || !lastLiveEvent) return;
    const entry = liveEventToActivity(lastLiveEvent);
    if (entry) setActivity(prev => mergeActivity(prev, [entry]));
    if (!LIVE_REFETCH.has(lastLiveEvent.type)) return;
    const wait = Math.max(0, LIVE_THROTTLE_MS - (Date.now() - lastLiveRefresh.current));
    if (liveTimer.current) return; // a refresh is already scheduled
    liveTimer.current = setTimeout(() => {
      liveTimer.current = null;
      lastLiveRefresh.current = Date.now();
      fetchRef.current?.({ quiet: true, reason: `live: ${lastLiveEvent.type}` });
    }, wait);
  }, [liveVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- fullscreen state follows the browser ----
  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // ---- derived ----
  const series = useMemo(() => periodSeries(history, period), [history, period]);
  const costAnalysis = useMemo(() => analyseCost(costRows), [costRows]);
  const hot = useMemo(() => buildHotspots(issues), [issues]);
  const health = useMemo(() => healthScore(kpis, settings.targets), [kpis, settings.targets]);
  const alerts = useMemo(() => kpiAlerts(kpis, settings.targets), [kpis, settings.targets]);
  const alertByKpi = useMemo(() => Object.fromEntries(alerts.map(a => [a.kpi, a])), [alerts]);

  // toast only when an alert first appears (not on every refresh, not on first load)
  useEffect(() => {
    if (!kpis) return;
    const keys = new Set(alerts.map(a => a.key));
    if (knownAlerts.current && settings.notifications) {
      alerts.filter(a => !knownAlerts.current.has(a.key)).forEach(a => toast({ title: a.title, description: a.message, status: a.status, duration: 6000, isClosable: true, position: 'top-right' }));
    }
    knownAlerts.current = keys;
  }, [alerts, kpis, settings.notifications, toast]);

  const targetOf = useCallback((def) => (def.target ? num(settings.targets?.[def.target]) : null), [settings.targets]);

  const cards = useMemo(() => KPI_DEFS.map(def => ({
    def,
    value: kpis ? kpis[def.key] : null,
    trend: kpis ? kpis[`${def.key}_trend`] : null,
    sparkline: series[def.key]?.current || [],
    previous: series[def.key]?.previousAgg ?? null,
    target: targetOf(def),
    alert: alertByKpi[def.key] || null,
  })), [kpis, series, targetOf, alertByKpi]);

  const visibleCards = useMemo(() => cards.filter((c) => {
    if (searchQuery && !c.def.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filters.includes('attention') && !c.alert) return false;
    if (filters.includes('trending') && !(isNum(c.trend) && Math.abs(c.trend) >= 5)) return false;
    if (filters.includes('starred') && !settings.favorites.includes(c.def.key)) return false;
    return true;
  }), [cards, searchQuery, filters, settings.favorites]);

  // DashboardAIInsights' forecast insight reads {predicted, confidence}; our forecast has an
  // interval instead of a made-up confidence, so confidence is left out on purpose.
  const insightPredictions = useMemo(() => (costAnalysis.forecast ? costAnalysis.forecast.points.map(p => ({ month: p.month, predicted: p.predicted })) : null), [costAnalysis.forecast]);
  const selectedCard = cards.find(c => c.def.key === selectedKpi) || null;

  // ---- handlers ----
  const ask = useCallback((q) => { if (onAskAI) onAskAI(q); }, [onAskAI]);
  const askKpi = useCallback((c) => ask(`${c.def.title} is ${fmtKpi(c.def, c.value)}${isNum(c.trend) ? ` (${fmtSigned(c.trend)} vs the previous period)` : ''} for ${period.start} to ${period.end}. What is driving it and what should we do?`), [ask, period]);
  const handleRefresh = useCallback(() => { log('Manual refresh'); fetchAll({ quiet: true, reason: 'manual refresh' }); }, [fetchAll, log]);
  const toggleFilter = useCallback((f) => setFilters(prev => (prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f])), []);
  const toggleFavorite = useCallback((key) => updateSettings({ favorites: settings.favorites.includes(key) ? settings.favorites.filter(k => k !== key) : [...settings.favorites, key] }), [settings.favorites, updateSettings]);
  const openDrilldown = useCallback((key) => { if (!settings.drilldown) return; setSelectedKpi(key); drilldownDlg.onOpen(); }, [settings.drilldown, drilldownDlg]);
  const setMapStyle = useCallback((style) => { updateSettings({ mapStyle: style }); setMapKey(`map-${style}-${Date.now()}`); }, [updateSettings]);

  const handleFullscreen = useCallback(() => {
    try {
      if (!document.fullscreenElement) dashboardRef.current?.requestFullscreen?.();
      else document.exitFullscreen?.();
    } catch (e) { toast({ title: 'Fullscreen is not available here', status: 'info', duration: 2500 }); }
  }, [toast]);

  const handleReport = useCallback(async () => {
    reportDlg.onOpen();
    setReport({ text: '', source: null, loading: true, error: null });
    try {
      const res = await api.get('/reports/executive-summary', { params: { days: period.span, startDate: period.start, endDate: period.end }, timeout: 90000 });
      const body = res?.data;
      if (!body?.success || !body?.report_markdown) throw new Error(body?.message || 'empty report');
      if (mounted.current) setReport({ text: body.report_markdown, source: body.model ? `Reports service · ${body.model}` : 'Reports service', loading: false, error: null });
    } catch (primary) {
      try {
        const b = await mlOpsService.getBriefing({ startDate: period.start, endDate: period.end });
        const src = b?.narrative?.source === 'llm' || b?.narrative?.model ? `FlowSense AI briefing · ${b.narrative.model || 'language model'}` : 'FlowSense AI briefing · built-in summary (language model offline)';
        if (mounted.current) setReport({ text: briefingToMarkdown(b), source: src, loading: false, error: null });
        log(`Reports service unavailable (${primary.message}); used the ML Ops briefing`, 'warning');
      } catch (fallback) {
        if (mounted.current) setReport({ text: '', source: null, loading: false, error: fallback.message || 'The briefing could not be generated.' });
      }
    }
  }, [period, reportDlg, log]);

  const copyReport = useCallback(async () => {
    try { await navigator.clipboard.writeText(report.text); toast({ title: 'Copied to clipboard', status: 'success', duration: 2000 }); } catch (_) { toast({ title: 'Clipboard is not available', status: 'warning', duration: 2500 }); }
  }, [report.text, toast]);

  const download = useCallback((name, content, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, []);
  const stamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

  const exportCSV = useCallback(async () => {
    try {
      const { default: Papa } = await import('papaparse');
      const kpiRows = cards.map(c => ({ section: 'KPI', name: c.def.title, value: c.value ?? '', change_vs_previous_pct: c.trend ?? '', previous_period: c.previous ?? '', target: c.target ?? '' }));
      const costOut = costAnalysis.chart.map(r => ({ section: 'Monthly cost', name: r.month, value: r.totalCost ?? '', change_vs_previous_pct: '', previous_period: '', target: '' }));
      const fc = (costAnalysis.forecast?.points || []).map(p => ({ section: 'Cost forecast', name: p.month, value: Math.round(p.predicted), change_vs_previous_pct: '', previous_period: `95%: ${Math.round(p.lower)}-${Math.round(p.upper)}`, target: '' }));
      const rk = (risk?.assets || []).map(a => ({ section: 'Asset risk', name: a.assetName, value: a.riskScore ?? '', change_vs_previous_pct: '', previous_period: `RUL ${a.rulDays ?? ''} d`, target: '' }));
      download(`executive-overview-${stamp()}.csv`, Papa.unparse([...kpiRows, ...costOut, ...fc, ...rk]), 'text/csv;charset=utf-8');
      toast({ title: 'CSV exported', status: 'success', duration: 2000 });
    } catch (e) { toast({ title: 'CSV export failed', description: e.message, status: 'error', duration: 4000 }); }
  }, [cards, costAnalysis, risk, download, toast]);

  const exportJSON = useCallback(() => {
    try {
      const out = { generatedAt: new Date().toISOString(), period, kpis, health, alerts, kpiHistory: history, cost: costAnalysis, hotspots: hot, assetRisk: risk, aiStatus: aiHealth, serverInsights };
      download(`executive-overview-${stamp()}.json`, JSON.stringify(out, null, 2), 'application/json');
      toast({ title: 'JSON exported', status: 'success', duration: 2000 });
    } catch (e) { toast({ title: 'JSON export failed', description: e.message, status: 'error', duration: 4000 }); }
  }, [period, kpis, health, alerts, history, costAnalysis, hot, risk, aiHealth, serverInsights, download, toast]);

  const exportPDF = useCallback(async () => {
    try {
      toast({ title: 'Preparing PDF…', status: 'info', duration: 1500 });
      const [{ default: html2canvas }, { default: JsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
      const canvas = await html2canvas(dashboardRef.current, { scale: 1.5, useCORS: true, logging: false });
      const pdf = new JsPDF('l', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth(); const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      const img = canvas.toDataURL('image/png');
      for (let y = 0; y < imgH; y += pageH) { if (y > 0) pdf.addPage(); pdf.addImage(img, 'PNG', 0, -y, pageW, imgH); }
      pdf.save(`executive-overview-${stamp()}.pdf`);
      toast({ title: 'PDF exported', status: 'success', duration: 2000 });
    } catch (e) { toast({ title: 'PDF export failed', description: e.message, status: 'error', duration: 4000 }); }
  }, [toast]);

  // ---- full error state (nothing loaded at all) ----
  if (!loading && !hasData.current && failed.length >= 3) {
    return (
      <Center minH="50vh" p={8} data-testid="exec-error">
        <VStack spacing={4}>
          <Icon as={MdError} boxSize={14} color="red.400" />
          <Heading size="md" color="red.500">The executive overview could not load</Heading>
          <Text color="gray.500" textAlign="center" maxW="420px">Unavailable: {failed.join(', ')}. Check your connection and that the API is running.</Text>
          <Button leftIcon={<MdRefresh />} colorScheme="blue" onClick={() => fetchAll({ reason: 'retry' })} borderRadius="full">Retry</Button>
        </VStack>
      </Center>
    );
  }

  const aiStatus = aiHealth?.status || 'unknown';
  const aiColor = { ok: 'green', degraded: 'orange', offline: 'red', unknown: 'gray' }[aiStatus] || 'gray';
  const aiLabel = { ok: 'Online', degraded: 'Degraded', offline: 'Offline', unknown: 'Unknown' }[aiStatus] || 'Unknown';
  const liveConnected = Boolean(liveVersion);
  const MAP_STYLES = [{ key: 'streets', icon: MdMap }, { key: 'satellite', icon: MdSatellite }, { key: 'dark', icon: MdLayers }, { key: 'topo', icon: MdTerrain }];
  const FILTERS = [{ id: 'attention', label: 'Needs attention', icon: MdError, color: 'red' }, { id: 'trending', label: 'Moving ≥ 5 %', icon: MdTrendingUp, color: 'green' }, { id: 'starred', label: 'Starred', icon: FaStar, color: 'yellow' }];

  return (
    <Box bg={pageBg} p={{ base: 3, md: 5 }} ref={dashboardRef} data-testid="executive-overview">
      <VStack spacing={6} align="stretch" maxW="1800px" mx="auto">
        {/* ===== HEADER ===== */}
        <GlassCard>
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} p={5}>
            <HStack spacing={4}>
              <Box p={3} bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)" borderRadius="xl"><MdDashboard color="white" size={28} /></Box>
              <VStack align="start" spacing={1}>
                <Heading as="h2" size="lg" bgGradient={headerGradient} bgClip="text">Executive Overview</Heading>
                <HStack spacing={2} flexWrap="wrap">
                  <PulseIndicator color={liveConnected ? 'green' : 'gray'} label={liveConnected ? 'Live' : 'Polling'} />
                  <Badge colorScheme="blue" variant="subtle" borderRadius="full" fontSize="xs">{period.start} → {period.end}</Badge>
                  <Tooltip label={aiHealth?.message || 'ML engine status from the AI control plane'} hasArrow>
                    <Badge colorScheme={aiColor} variant="subtle" borderRadius="full" fontSize="xs" data-testid="ai-status-badge">AI: {aiLabel}</Badge>
                  </Tooltip>
                  {settings.autoRefresh && <Badge variant="subtle" borderRadius="full" fontSize="xs">Auto {Math.round(settings.refreshInterval / 60000)} min</Badge>}
                  {lastUpdated && <Text fontSize="xs" color="gray.500">Updated {timeAgo(lastUpdated)}</Text>}
                  {refreshing && <Spinner size="xs" color="blue.400" data-testid="quiet-refresh" />}
                </HStack>
              </VStack>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              <InputGroup size="sm" w={{ base: '100%', md: '190px' }}>
                <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                <Input placeholder="Search KPIs…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} borderRadius="full" bg={subtleBg} aria-label="Search KPIs" />
                {searchQuery && <InputRightElement><IconButton icon={<MdClose />} size="xs" variant="ghost" onClick={() => setSearchQuery('')} aria-label="Clear search" /></InputRightElement>}
              </InputGroup>
              <ButtonGroup size="sm" isAttached variant="outline">
                <Tooltip label="Dashboard" hasArrow><IconButton icon={<MdDashboard />} onClick={() => setViewMode('dashboard')} isActive={viewMode === 'dashboard'} aria-label="Dashboard view" /></Tooltip>
                <Tooltip label="KPI details" hasArrow><IconButton icon={<MdViewList />} onClick={() => setViewMode('details')} isActive={viewMode === 'details'} aria-label="Details view" /></Tooltip>
                {enableComparison && <Tooltip label="Compare periods" hasArrow><IconButton icon={<MdCompareArrows />} onClick={() => setViewMode('comparison')} isActive={viewMode === 'comparison'} aria-label="Comparison view" /></Tooltip>}
              </ButtonGroup>
              <ButtonGroup size="sm" isAttached variant="outline">
                <Tooltip label="Refresh" hasArrow><IconButton icon={<MdRefresh />} onClick={handleRefresh} isLoading={refreshing} aria-label="Refresh" /></Tooltip>
                <Tooltip label="AI insights" hasArrow><IconButton icon={<MdInsights />} onClick={insightsDlg.onOpen} colorScheme="purple" aria-label="AI insights" /></Tooltip>
                <Tooltip label="AI system monitor" hasArrow><IconButton icon={<MdSmartToy />} onClick={monitorDlg.onOpen} colorScheme="blue" aria-label="AI system monitor" /></Tooltip>
                <Tooltip label="Executive briefing" hasArrow><IconButton icon={<MdDescription />} onClick={handleReport} colorScheme="teal" aria-label="Executive briefing" data-guide="exec-briefing" /></Tooltip>
                {enableExport && (
                  <Menu>
                    <MenuButton as={IconButton} icon={<MdDownload />} aria-label="Export" />
                    <MenuList>
                      <MenuItem icon={<MdPrint />} onClick={exportPDF}>PDF snapshot</MenuItem>
                      <MenuItem icon={<FaChartBar />} onClick={exportCSV}>CSV data</MenuItem>
                      <MenuItem icon={<FaDatabase />} onClick={exportJSON}>JSON (full)</MenuItem>
                    </MenuList>
                  </Menu>
                )}
                <Tooltip label="Settings" hasArrow><IconButton icon={<MdSettings />} onClick={settingsDlg.onOpen} aria-label="Settings" /></Tooltip>
                {enableDebug && <Tooltip label="Diagnostics" hasArrow><IconButton icon={<MdBugReport />} onClick={() => setShowDebug(s => !s)} colorScheme={showDebug ? 'red' : 'gray'} aria-label="Diagnostics" /></Tooltip>}
                <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} hasArrow><IconButton icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />} onClick={handleFullscreen} aria-label="Fullscreen" /></Tooltip>
              </ButtonGroup>
            </HStack>
          </Flex>
        </GlassCard>

        {failed.length > 0 && hasData.current && (
          <Alert status="warning" borderRadius="lg" data-testid="partial-failure"><AlertIcon /><AlertDescription fontSize="sm">Some data could not be loaded: {failed.join(', ')}. The rest of the page is current.</AlertDescription></Alert>
        )}

        {/* Quick filters */}
        <HStack spacing={2} flexWrap="wrap">
          <HStack spacing={1}><Icon as={MdFilterList} color="gray.500" /><Text fontSize="sm" fontWeight="semibold" color="gray.600">KPI filters:</Text></HStack>
          {FILTERS.map(f => (
            <Button key={f.id} size="xs" leftIcon={<Icon as={f.icon} boxSize={3} />} variant={filters.includes(f.id) ? 'solid' : 'outline'} colorScheme={f.color} borderRadius="full" onClick={() => toggleFilter(f.id)} aria-pressed={filters.includes(f.id)}>{f.label}</Button>
          ))}
          {filters.length > 0 && <Button size="xs" variant="ghost" onClick={() => setFilters([])} borderRadius="full">Clear</Button>}
        </HStack>

        {enableDebug && <DebugPanel isOpen={showDebug} onClose={() => setShowDebug(false)} logs={logs} perf={perf} />}

        <Grid templateColumns={{ base: '1fr', xl: '5fr 2fr' }} gap={6}>
          {/* LEFT */}
          <VStack align="stretch" spacing={6} minW={0}>
            <Box data-guide="exec-kpis">
              <HStack justify="space-between" mb={3}>
                <Heading size="md">Key performance indicators</Heading>
                {(searchQuery || filters.length > 0) && <Text fontSize="sm" color="gray.500">Showing {visibleCards.length} of {cards.length}</Text>}
              </HStack>
              {visibleCards.length === 0 ? <EmptyState icon={MdFilterList} text="No KPI matches the current search or filters." /> : (
                <SimpleGrid columns={kpiColumns} spacing={4}>
                  {visibleCards.map(c => (
                    <KpiCard key={c.def.key} {...c} isLoading={loading} isFavorite={settings.favorites.includes(c.def.key)}
                      onToggleFavorite={() => toggleFavorite(c.def.key)} onClick={settings.drilldown ? () => openDrilldown(c.def.key) : null}
                      onAsk={onAskAI ? () => askKpi(c) : null} />
                  ))}
                </SimpleGrid>
              )}
            </Box>

            {viewMode === 'details' && <KpiDetails cards={cards} />}
            {viewMode === 'comparison' && <PeriodComparison series={series} period={period} />}

            <HealthScorecard health={health} />

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <CostTrendCard analysis={costAnalysis} loading={loading} months={settings.costMonths} onAsk={onAskAI ? ask : null} />
              <VStack spacing={6} align="stretch">
                <GlassCard p={5} minH="200px">
                  <Suspense fallback={<Skeleton height="200px" />}><CostAnomalyWidget /></Suspense>
                </GlassCard>
                <TopCostAssets rows={problemAssets} loading={loading} />
              </VStack>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <TrendAnalysisCard trend={costAnalysis.trend} />
              <AnomalyTable anomalies={costAnalysis.anomalies} onAsk={onAskAI ? ask : null} />
            </SimpleGrid>

            {/* Map */}
            <GlassCard p={5}>
              <SectionTitle icon={MdMap} color="orange" title="Geographic zones & hotspots" subtitle="Live active alerts and zone assignments"
                right={(
                  <HStack spacing={2}>
                    <ButtonGroup size="xs" isAttached variant="outline">
                      {MAP_STYLES.map(m => <Tooltip key={m.key} label={m.key} hasArrow><IconButton icon={<m.icon />} isActive={settings.mapStyle === m.key} onClick={() => setMapStyle(m.key)} aria-label={`Map style ${m.key}`} /></Tooltip>)}
                    </ButtonGroup>
                    <FormControl display="flex" alignItems="center" w="auto">
                      <FormLabel fontSize="xs" mb={0} mr={1} htmlFor="exec-radius">Radius</FormLabel>
                      <Switch id="exec-radius" size="sm" isChecked={settings.showMapRadius} onChange={(e) => updateSettings({ showMapRadius: e.target.checked })} colorScheme="orange" />
                    </FormControl>
                  </HStack>
                )} />
              <Box h={{ base: '350px', md: '480px' }} borderRadius="xl" overflow="hidden" borderWidth="1px">
                <Suspense fallback={<Center h="100%"><Spinner size="xl" color="orange.400" /></Center>}>
                  <GeospatialHotspotMap mapStyle={settings.mapStyle} showMapRadius={settings.showMapRadius} mapKey={mapKey} />
                </Suspense>
              </Box>
            </GlassCard>
          </VStack>

          {/* RIGHT */}
          <VStack align="stretch" spacing={6} minW={0}>
            <AssetRiskPanel risk={risk} loading={loading} error={riskError} onAsk={onAskAI ? ask : null} />
            <Box data-guide="exec-insights">
              <DashboardAIInsights kpis={kpis} costData={costAnalysis.chart} predictions={insightPredictions} aiHealth={aiHealth} aiModels={aiModels} serverInsights={serverInsights} onAskAI={onAskAI} />
            </Box>
            <ActivityFeed items={activity} loading={loading} liveConnected={liveConnected} />
            <DataQuality kpis={kpis} history={history} costRows={costRows} hot={hot} aiStatus={aiHealth?.status || null} />
            <HotspotStats hot={hot} />
          </VStack>
        </Grid>

        {/* Footer */}
        <GlassCard p={4}>
          <HStack justify="space-between" flexWrap="wrap" spacing={4} fontSize="xs" color="gray.500">
            <HStack spacing={4} flexWrap="wrap">
              <HStack><PulseIndicator color={aiColor} size="8px" /><Text>AI engine: <b>{aiLabel}</b></Text></HStack>
              <Text>Assistant: <b>{aiHealth ? (aiHealth.assistantOnline ? 'online' : 'built-in answers') : '—'}</b></Text>
              <Text>Models in production: <b>{aiHealth?.inProduction ?? '—'}{isNum(aiHealth?.trainable) ? ` / ${aiHealth.trainable}` : ''}</b></Text>
              <Text>Last load: <b>{perf.loadMs === null ? '—' : `${perf.loadMs} ms`}</b></Text>
              <Text>Events: <b>{activity.length}</b></Text>
            </HStack>
            <Text>Executive Overview v5 — FlowSense Analytics</Text>
          </HStack>
        </GlassCard>

        {/* ===== MODALS ===== */}
        <Modal isOpen={settingsDlg.isOpen} onClose={settingsDlg.onClose} size="lg">
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent borderRadius="xl">
            <ModalHeader><HStack><Icon as={MdSettings} /><Text>Dashboard settings</Text></HStack></ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Heading size="xs" color="gray.500" textTransform="uppercase">Data refresh</Heading>
                <FormControl display="flex" justifyContent="space-between" alignItems="center"><FormLabel mb={0} htmlFor="s-auto">Auto refresh</FormLabel><Switch id="s-auto" isChecked={settings.autoRefresh} onChange={(e) => updateSettings({ autoRefresh: e.target.checked })} /></FormControl>
                {settings.autoRefresh && (
                  <FormControl><FormLabel fontSize="sm" htmlFor="s-int">Interval</FormLabel>
                    <Select id="s-int" value={settings.refreshInterval} onChange={(e) => updateSettings({ refreshInterval: Number(e.target.value) })}>
                      <option value={60000}>1 minute</option><option value={300000}>5 minutes</option><option value={600000}>10 minutes</option><option value={1800000}>30 minutes</option>
                    </Select>
                  </FormControl>
                )}
                <Text fontSize="xs" color="gray.500">Live events (new alerts, predictions, model changes) refresh the page on their own, at most every {LIVE_THROTTLE_MS / 1000} s.</Text>
                <Divider />
                <Heading size="xs" color="gray.500" textTransform="uppercase">Targets</Heading>
                <SimpleGrid columns={3} spacing={3}>
                  <FormControl><FormLabel fontSize="xs" htmlFor="t-up">Availability %</FormLabel><NumberInput id="t-up" size="sm" min={1} max={100} value={settings.targets.uptime ?? ''} onChange={(_, v) => updateSettings({ targets: { uptime: Number.isFinite(v) ? v : null } })}><NumberInputField /></NumberInput></FormControl>
                  <FormControl><FormLabel fontSize="xs" htmlFor="t-sla">SLA %</FormLabel><NumberInput id="t-sla" size="sm" min={1} max={100} value={settings.targets.sla ?? ''} onChange={(_, v) => updateSettings({ targets: { sla: Number.isFinite(v) ? v : null } })}><NumberInputField /></NumberInput></FormControl>
                  <FormControl><FormLabel fontSize="xs" htmlFor="t-bud">Period budget $</FormLabel><NumberInput id="t-bud" size="sm" min={0} value={settings.targets.budget ?? ''} onChange={(_, v) => updateSettings({ targets: { budget: Number.isFinite(v) && v > 0 ? v : null } })}><NumberInputField placeholder="none" /></NumberInput></FormControl>
                </SimpleGrid>
                <Divider />
                <Heading size="xs" color="gray.500" textTransform="uppercase">Display</Heading>
                <FormControl display="flex" justifyContent="space-between" alignItems="center"><FormLabel mb={0} htmlFor="s-not">Alert notifications</FormLabel><Switch id="s-not" isChecked={settings.notifications} onChange={(e) => updateSettings({ notifications: e.target.checked })} /></FormControl>
                <FormControl display="flex" justifyContent="space-between" alignItems="center"><FormLabel mb={0} htmlFor="s-dd">KPI drill-down on click</FormLabel><Switch id="s-dd" isChecked={settings.drilldown} onChange={(e) => updateSettings({ drilldown: e.target.checked })} /></FormControl>
                <FormControl><FormLabel fontSize="sm" htmlFor="s-cm">Cost history window</FormLabel>
                  <Select id="s-cm" value={settings.costMonths} onChange={(e) => updateSettings({ costMonths: Number(e.target.value) })}>
                    <option value={6}>6 months</option><option value={12}>12 months</option><option value={24}>24 months</option>
                  </Select>
                </FormControl>
                <FormControl><FormLabel fontSize="sm" htmlFor="s-map">Map style</FormLabel>
                  <Select id="s-map" value={settings.mapStyle} onChange={(e) => setMapStyle(e.target.value)}>
                    <option value="streets">Streets</option><option value="satellite">Satellite</option><option value="dark">Dark</option><option value="topo">Topographic</option>
                  </Select>
                </FormControl>
                <Button size="sm" variant="outline" leftIcon={<MdAutorenew />} onClick={() => { updateSettings({ ...DEFAULT_SETTINGS, refreshInterval: defaultRefreshInterval }); toast({ title: 'Settings reset', status: 'info', duration: 1500 }); }}>Reset to defaults</Button>
              </VStack>
            </ModalBody>
            <ModalFooter><Button colorScheme="blue" onClick={settingsDlg.onClose} borderRadius="full">Done</Button></ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={drilldownDlg.isOpen} onClose={drilldownDlg.onClose} size="xl">
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent borderRadius="xl">
            <ModalHeader><HStack><Icon as={MdAnalytics} color="blue.500" /><Text>{selectedCard?.def.title || 'KPI'} — detail</Text></HStack></ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedCard && (() => {
                const st = seriesStats(selectedCard.sparkline);
                const prev = series[selectedCard.def.key]?.previous || [];
                const data = selectedCard.sparkline.map((p, i) => ({ date: p.date, value: p.value, previous: prev[i]?.value ?? null }));
                const t = num(selectedCard.trend);
                const good = t === null ? null : (t > 0) === (selectedCard.def.better === 'higher');
                return (
                  <VStack align="stretch" spacing={4} data-testid="kpi-drilldown">
                    <SimpleGrid columns={3} spacing={3}>
                      <Box p={3} bg={statBg} borderRadius="lg"><Text fontSize="xs" color="gray.500">Current</Text><Text fontSize="xl" fontWeight="bold">{fmtKpi(selectedCard.def, selectedCard.value)}</Text></Box>
                      <Box p={3} bg={statBg} borderRadius="lg"><Text fontSize="xs" color="gray.500">vs previous period</Text><Text fontSize="xl" fontWeight="bold" color={good === null ? undefined : good ? 'green.500' : 'red.500'}>{fmtSigned(t)}</Text></Box>
                      <Box p={3} bg={statBg} borderRadius="lg"><Text fontSize="xs" color="gray.500">Previous period</Text><Text fontSize="xl" fontWeight="bold">{fmtKpi(selectedCard.def, selectedCard.previous)}</Text></Box>
                    </SimpleGrid>
                    {data.length > 1 ? (
                      <Box height="260px">
                        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                          <ComposedChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="date" fontSize={10} />
                            <YAxis fontSize={11} />
                            <RechartsTooltip formatter={(v, name) => [isNum(v) ? fmtKpi(selectedCard.def, v) : '—', name]} />
                            <Legend />
                            <Area type="monotone" dataKey="value" name="This period" stroke={CHART.primary} fill={CHART.primary} fillOpacity={0.15} strokeWidth={2} connectNulls={false} />
                            <Line type="monotone" dataKey="previous" name="Previous period (same day)" stroke={CHART.muted} strokeDasharray="4 4" dot={false} connectNulls={false} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : <EmptyState text="No daily history for this KPI in the selected period." />}
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} fontSize="xs">
                      <Box><Text color="gray.500">Days with data</Text><Text fontWeight="bold">{st.n}</Text></Box>
                      <Box><Text color="gray.500">Daily mean</Text><Text fontWeight="bold">{fmtNum(st.mean, 2)}</Text></Box>
                      <Box><Text color="gray.500">Min / Max</Text><Text fontWeight="bold">{fmtNum(st.min, 2)} / {fmtNum(st.max, 2)}</Text></Box>
                      <Box><Text color="gray.500">Variation (CV)</Text><Text fontWeight="bold">{fmtPct(st.cv)}</Text></Box>
                    </SimpleGrid>
                    <Box p={4} bg={recommendBg} borderRadius="lg" borderLeftWidth="4px" borderLeftColor="blue.400">
                      <HStack mb={1}><Icon as={MdLightbulb} color="blue.500" /><Text fontSize="sm" fontWeight="bold">What this means</Text><Badge variant="outline" fontWeight="normal" textTransform="none">rule-based</Badge></HStack>
                      <Text fontSize="sm">
                        {t === null ? 'There is no comparable previous period yet, so the direction of this KPI cannot be judged.'
                          : good ? `${selectedCard.def.title} moved ${fmtSigned(t)} in the right direction compared with the previous period.`
                            : `${selectedCard.def.title} moved ${fmtSigned(t)} in the wrong direction compared with the previous period. Review what changed in operations during ${period.start} – ${period.end}.`}
                        {selectedCard.alert ? ` ${selectedCard.alert.message}` : ''}
                      </Text>
                      {onAskAI && <Button mt={2} size="xs" colorScheme="purple" variant="outline" leftIcon={<MdQuestionAnswer />} onClick={() => askKpi(selectedCard)}>Ask FlowSense AI</Button>}
                    </Box>
                  </VStack>
                );
              })()}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={drilldownDlg.onClose}>Close</Button>
              <Button colorScheme="blue" ml={3} onClick={() => { drilldownDlg.onClose(); setIsFullReportOpen(true); }}>View full report</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={insightsDlg.isOpen} onClose={insightsDlg.onClose} size="2xl">
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent borderRadius="xl">
            <ModalHeader><HStack><Icon as={MdInsights} color="purple.500" /><Text>AI-powered insights</Text></HStack></ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              <DashboardAIInsights kpis={kpis} costData={costAnalysis.chart} predictions={insightPredictions} aiHealth={aiHealth} aiModels={aiModels} serverInsights={serverInsights} onAskAI={onAskAI} onClose={insightsDlg.onClose} />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Modal isOpen={monitorDlg.isOpen} onClose={monitorDlg.onClose} size="6xl">
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent borderRadius="xl">
            <ModalHeader><HStack><Icon as={MdSmartToy} color="blue.500" /><Text>AI system monitor</Text></HStack></ModalHeader>
            <ModalCloseButton />
            <ModalBody p={6} bg={modalBodyBg}>{monitorDlg.isOpen && <AIMetricsDashboard liveVersion={liveVersion} onAskAI={onAskAI} />}</ModalBody>
          </ModalContent>
        </Modal>

        <Modal isOpen={reportDlg.isOpen} onClose={reportDlg.onClose} size="4xl" scrollBehavior="inside">
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent borderRadius="xl">
            <ModalHeader>
              <HStack flexWrap="wrap"><Icon as={MdDescription} color="teal.500" /><Text>Executive briefing</Text><Badge colorScheme="teal" variant="subtle" borderRadius="full">{period.start} → {period.end}</Badge></HStack>
              {report.source && <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1} data-testid="briefing-source">Source: {report.source}</Text>}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={6} bg={modalBodyBg}>
              {report.loading ? (
                <VStack spacing={4} align="stretch" data-testid="briefing-loading">
                  <HStack justify="center"><Spinner color="teal.500" /><Text color="teal.600" fontWeight="bold">Drafting the briefing from live data…</Text></HStack>
                  <Skeleton height="28px" width="50%" /><SkeletonText noOfLines={8} spacing={3} />
                </VStack>
              ) : report.error ? (
                <Alert status="error" borderRadius="md"><AlertIcon />{report.error}</Alert>
              ) : <Box p={2}><MarkdownLite text={report.text} /></Box>}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={reportDlg.onClose}>Close</Button>
              <Button variant="outline" mr={3} leftIcon={<MdDownload />} isDisabled={!report.text} onClick={() => download(`executive-briefing-${period.start}-${period.end}.md`, report.text, 'text/markdown')}>Download</Button>
              <Button colorScheme="teal" leftIcon={<MdContentCopy />} isDisabled={report.loading || !report.text} onClick={copyReport}>Copy</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <KPIFullReportModal isOpen={isFullReportOpen} onClose={() => setIsFullReportOpen(false)} kpiKey={selectedKpi} kpis={kpis || {}} />
      </VStack>
    </Box>
  );
}

export default ExecutiveOverviewDashboard;
